import { subClientsApi } from "#/api/subClientsApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { SubClient } from "#/types/subClient";
import { logger } from "#/utils/logger";
import { Button, Modal } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  opened: boolean;
  onClose: () => void;
  subClient: SubClient | undefined;
  onSuccess: () => void;
}

const DeleteSubClientModal = ({ opened, onClose, subClient, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => subClientsApi.delete(id),
    onSuccess: () => {
      showSuccessNotification({
        message: t("subClients.delete.success"),
      });
      onClose();
      onSuccess();
    },
    onError: (error) => {
      logger.error("DeleteSubClientModal error:", error);
      showErrorNotification({
        message: t("subClients.delete.error"),
      });
    },
  });

  const handleConfirm = () => {
    if (subClient) deleteMutation.mutate(subClient.id);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t("subClients.delete.title")}
      closeOnClickOutside={false}
    >
      <p>
        {t("subClients.delete.confirmation")} <strong>{subClient?.name}</strong>?
      </p>
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button
          variant="outline"
          color="black"
          onClick={onClose}
          disabled={deleteMutation.isPending}
        >
          {t("common.cancel")}
        </Button>
        <PermissionTooltip disabled={!canDelete()}>
          <Button
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={handleConfirm}
            loading={deleteMutation.isPending}
            disabled={!canDelete()}
          >
            {t("common.delete")}
          </Button>
        </PermissionTooltip>
      </div>
    </Modal>
  );
};

export default DeleteSubClientModal;
